<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useSearchStore } from "@/stores/search";
import FilterBar from "@/components/FilterBar/FilterBar.ce.vue";
import QuickSearch from "@/components/QuickSearch/QuickSearch.vue";
import FeatureFlag from "@/components/FeatureFlag/FeatureFlag.ce.vue";
import SearchBar from "@/components/SearchBar/SearchBar.vue";
import useIntersectionObserver from "@/composables/useIntersectionObserver";

const props = defineProps({
    isIframe: {
        type: Boolean,
        default: false,
    },
    teaserHeight: {
        type: Number,
        default: 0,
    },
});

const emit = defineEmits(["getTeaserOffset"]);

const store = useSearchStore();
const target = ref(null);
const isSearchBoxIntersect = ref(true);
const searchBox = ref<HTMLElement>();
const topPosition = ref();
const searchBoxHeight = ref();

const currentSearchTerm = computed(() => {
    return store.getQuickSearchValue;
});

function intersectCallback(entry: IntersectionObserverEntry) {
    isSearchBoxIntersect.value = entry.isIntersecting;
}

const isPageScrollUp = ref(false);
const isPageScrollDown = ref(false);
let previousScrollY = 0;
const handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (isSearchBoxIntersect.value) {
        if (window.scrollY <= topPosition.value) {
            isPageScrollUp.value = false;
        }
        isPageScrollDown.value = false;
        return;
    }

    if (isPageScrollUp.value && currentScrollY > previousScrollY) {
        //the scrollDown class should only be set when the scrollUp already happened
        isPageScrollDown.value = true;
        isPageScrollUp.value = false;
    } else if (currentScrollY < previousScrollY) {
        isPageScrollUp.value = true;
        isPageScrollDown.value = false;
    }
    previousScrollY = currentScrollY;
};

function getSearchBarOffset() {
    emit("getTeaserOffset");
    topPosition.value =
        props.teaserHeight +
        parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
                "--header-height"
            )
        );
}

async function setSearchBoxHeight() {
    getSearchBarOffset();
    searchBoxHeight.value = searchBox.value?.offsetHeight + "px";
}

onMounted(async () => {
    if (target.value) {
        useIntersectionObserver.observeElemement(
            target.value,
            intersectCallback
        );
    }
    await setSearchBoxHeight();

    window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
});
</script>
<template>
    <div
        class="[ c-search-box-placeholder ]"
        :style="'height:' + searchBoxHeight"
    ></div>
    <div
        ref="searchBox"
        class="[ c-search-box__shadow c-search-box__shadow--sticky ]"
        :class="{
            'is-slide-in': isPageScrollUp,
            'is-slide-out': isPageScrollDown && !isSearchBoxIntersect,
            'is-iframe': isIframe,
        }"
    >
        <div>
            <feature-flag option="searchBarV1">
                <quick-search
                    :current-value="currentSearchTerm"
                    :key="currentSearchTerm"
                ></quick-search>
            </feature-flag>
            <feature-flag option="searchBarV2">
                <search-bar />
            </feature-flag>
            <div class="[ c-search-box__wrap ]">
                <filter-bar
                    @update="setSearchBoxHeight"
                    :key="currentSearchTerm"
                ></filter-bar>
            </div>
        </div>
    </div>
    <span ref="target"></span>
</template>
